/*
  Header v2 behavior. Loaded with defer by sections/header.liquid, after global.js.

  <header-mega> wraps the header. Each [data-mega-item] holds a [data-mega-button] (with
  aria-expanded and aria-controls) and its [data-mega-panel]. Only one panel is open at a time.
  - Pointer: a panel opens after OPEN_DELAY of hovering its item and closes CLOSE_DELAY after
    the pointer leaves it.
  - Click, tap, Enter and Space toggle a panel; clicking a panel that hover opened keeps it open.
  - Keyboard: Arrow Down opens a panel and moves into it, Escape closes it and returns focus to
    its button, Arrow Left/Right, Home and End move between the rail items. Keyboard focus
    inside an item keeps it open; focus or a click elsewhere closes it.
  - Product tiles: a panel's [data-panel-tiles] lists hold placeholders and name their
    collection URL. When the pointer first enters the rail, every rail panel's tiles are
    fetched through the Section Rendering API (sections/header-panel-products.liquid); an item
    that is hovered, focused or opened fetches its own if they are not there yet. Tiles are
    kept for the rest of the page's life. A list whose fetch failed is hidden (a panel without
    groups lays out as a links-only panel) and tried again the next time its panel is used.

  The rail ([data-rail-list]) never wraps. Items that do not fit move, last first, into the
  trailing More item ([data-rail-more]), whose panel shows the [data-more-entry] of each moved
  item. External items (Cable Builder, Docs) move only after every other item has.

  <header-drawer-v2> is Ride's HeaderDrawer (global.js) for the burger menu, except that the
  category groups inside it are open <details> that expand and collapse natively.
*/
(() => {
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 200;
  // How long Arrow Down waits for a panel's tiles before it focuses whatever link is there.
  const FOCUS_WAIT = 600;
  const DESKTOP_QUERY = '(min-width: 1140px)';
  const TILES_SECTION = 'header-panel-products';

  const isVisible = (element) => element.getClientRects().length > 0;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HeaderMega extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.items = Array.from(this.querySelectorAll('[data-mega-item]'));
        this.railList = this.querySelector('[data-rail-list]');
        this.moreItem = this.querySelector('[data-rail-more]');
        this.openItem = null;
        this.pinned = false;
        this.hoverItem = null;
        this.tileRequests = new Map();

        this.items.forEach((item) => {
          item.addEventListener('pointerenter', (event) => this.onPointerEnter(event, item));
          item.addEventListener('pointerleave', (event) => this.onPointerLeave(event, item));
          this.buttonOf(item)?.addEventListener('click', () => this.onButtonClick(item));
        });
        this.railList?.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch') this.warmUpRail();
        });
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
      }

      document.addEventListener('pointerdown', this.onDocumentPointerDown);

      if (this.railList && this.moreItem) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        this.resizeObserver.observe(this.railList);
        if (document.fonts) document.fonts.ready.then(() => this.scheduleMeasure());
        this.measure();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      cancelAnimationFrame(this.measureFrame);
      this.clearTimers();
    }

    buttonOf(item) {
      return item.querySelector(':scope > [data-mega-button]');
    }

    panelOf(item) {
      return item.querySelector(':scope > [data-mega-panel]');
    }

    clearTimers() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
    }

    open(item, { pinned = false } = {}) {
      this.clearTimers();
      if (this.openItem && this.openItem !== item) this.close();

      this.openItem = item;
      this.pinned = pinned;
      item.classList.add('is-open');
      this.buttonOf(item)?.setAttribute('aria-expanded', 'true');
      this.loadTiles(item);

      // Keep tall panels inside the window; the panel scrolls instead.
      const panel = this.panelOf(item);
      if (panel) {
        const room = Math.floor(window.innerHeight - panel.getBoundingClientRect().top - 16);
        panel.style.setProperty('--panel-max-height', `${Math.max(240, room)}px`);
      }
    }

    /** Closes the open panel, if any. Also called by sticky-header when the header changes state. */
    close({ returnFocus = false } = {}) {
      this.clearTimers();
      const item = this.openItem;
      if (!item) return;

      this.openItem = null;
      this.pinned = false;
      item.classList.remove('is-open');
      const button = this.buttonOf(item);
      button?.setAttribute('aria-expanded', 'false');
      if (returnFocus) button?.focus();
    }

    hasKeyboardFocus(item) {
      try {
        return Boolean(item.querySelector(':focus-visible'));
      } catch (error) {
        return item.contains(document.activeElement);
      }
    }

    scheduleClose() {
      const item = this.openItem;
      if (!item || this.pinned || this.hoverItem === item || this.hasKeyboardFocus(item)) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    onPointerEnter(event, item) {
      if (event.pointerType === 'touch') return;
      this.hoverItem = item;
      this.clearTimers();
      // Start the fetch now, so the tiles are usually in place when the panel opens.
      this.loadTiles(item);
      if (this.openItem !== item) this.openTimer = setTimeout(() => this.open(item), OPEN_DELAY);
    }

    onPointerLeave(event, item) {
      if (event.pointerType === 'touch') return;
      if (this.hoverItem === item) this.hoverItem = null;
      clearTimeout(this.openTimer);
      this.scheduleClose();
    }

    onButtonClick(item) {
      if (this.openItem !== item) {
        this.open(item, { pinned: true });
      } else if (this.pinned) {
        this.close();
      } else {
        this.clearTimers();
        this.pinned = true;
      }
    }

    onDocumentPointerDown = (event) => {
      if (this.openItem && !this.openItem.contains(event.target)) this.close();
    };

    onFocusIn = (event) => {
      if (this.openItem && !this.openItem.contains(event.target)) this.close();
      // A focused rail button is about to be opened: fetch its tiles ahead of time.
      if (event.target.matches('[data-mega-button]')) this.loadTiles(event.target.parentElement);
    };

    onFocusOut = (event) => {
      const next = event.relatedTarget;
      if (this.openItem && next && !this.contains(next)) this.close();
    };

    onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!this.openItem) return;
        this.close({ returnFocus: this.openItem.contains(document.activeElement) });
        return;
      }

      const target = event.target;
      if (!target.matches('[data-rail-focus]')) return;

      if (event.key === 'ArrowDown' && target.matches('[data-mega-button]')) {
        const item = target.parentElement;
        event.preventDefault();
        this.open(item, { pinned: true });
        this.focusIntoPanel(item);
        return;
      }

      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const targets = Array.from(this.railList.querySelectorAll(':scope > li:not([hidden]) > [data-rail-focus]'));
      const index = targets.indexOf(target);
      if (index === -1) return;

      event.preventDefault();
      let next = index;
      if (event.key === 'ArrowLeft') next = (index - 1 + targets.length) % targets.length;
      if (event.key === 'ArrowRight') next = (index + 1) % targets.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = targets.length - 1;
      targets[next].focus();
    };

    /** Focuses the panel's first link once its tiles are in place, or after FOCUS_WAIT at the latest. */
    focusIntoPanel(item) {
      const button = this.buttonOf(item);
      const wait = new Promise((resolve) => setTimeout(resolve, FOCUS_WAIT));
      Promise.race([this.loadTiles(item), wait]).then(() => {
        if (this.openItem !== item || document.activeElement !== button) return;
        const first = Array.from(this.panelOf(item).querySelectorAll('a[href], button:not([disabled])')).find(isVisible);
        if (first) first.focus();
      });
    }

    /**
     * Fetches every rail panel's tiles the first time the pointer is on the rail, so they are
     * usually in place before the item under it has been hovered long enough to open.
     */
    warmUpRail() {
      if (this.warmedUp) return;
      this.warmedUp = true;
      this.railList.querySelectorAll('[data-mega-item]').forEach((item) => this.loadTiles(item));
    }

    /**
     * Fetches the product tiles of an item's panel on first use: every [data-panel-tiles] list in
     * it (one per collection group) at once. Resolves once each list is filled or has failed.
     */
    loadTiles(item) {
      const panel = this.panelOf(item);
      if (!panel) return Promise.resolve();
      const lists = Array.from(panel.querySelectorAll('[data-panel-tiles]'));
      return Promise.all(lists.map((list) => this.loadList(list)));
    }

    /**
     * Renders sections/header-panel-products.liquid at the list's collection URL and moves its
     * tiles into the list in place of the placeholders. One request per list at a time; a list
     * whose request failed hides its group until a later call succeeds.
     */
    loadList(list) {
      if (list.dataset.state === 'loaded') return Promise.resolve();
      if (this.tileRequests.has(list)) return this.tileRequests.get(list);

      const url = new URL(list.dataset.panelTiles, window.location.origin);
      url.searchParams.set('sections', TILES_SECTION);
      const request = fetch(url)
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error(response.status))))
        .then((sections) => {
          const rendered = new DOMParser().parseFromString(sections[TILES_SECTION] || '', 'text/html');
          const tiles = rendered.querySelectorAll('.header__tiles > li');
          if (tiles.length === 0) throw new Error('No tiles');
          const watched = this.openItem === list.closest('[data-mega-item]');
          list.replaceChildren(...tiles);
          // Fade the tiles in only when they replace placeholders the visitor is looking at.
          if (watched && !reducedMotion.matches) {
            tiles.forEach((tile) => tile.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: 'ease' }));
          }
          list.removeAttribute('aria-busy');
          list.dataset.state = 'loaded';
          this.showGroup(list, true);
        })
        .catch(() => {
          list.dataset.state = 'failed';
          this.showGroup(list, false);
        })
        .finally(() => this.tileRequests.delete(list));
      this.tileRequests.set(list, request);
      return request;
    }

    /** Shows or hides a list's group; a panel with every group hidden lays out as a links-only panel. */
    showGroup(list, visible) {
      const group = list.closest('.header__group');
      if (group) group.hidden = !visible;
      const panel = list.closest('[data-mega-panel]');
      const groups = panel?.querySelector('.header__groups');
      if (!groups) return;
      const anyVisible = Array.from(groups.children).some((child) => !child.hidden);
      groups.hidden = !anyVisible;
      panel.querySelector('.header__panel-inner')?.classList.toggle('header__panel-inner--links', !anyVisible);
    }

    scheduleMeasure() {
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = requestAnimationFrame(() => this.measure());
    }

    railFits() {
      return this.railList.scrollWidth <= this.railList.clientWidth + 1;
    }

    /** Priority plus: moves rail items that do not fit into More, last first. */
    measure() {
      const list = this.railList;
      if (!list || list.clientWidth === 0) return;

      const items = Array.from(list.querySelectorAll(':scope > [data-rail-item]'));
      const entries = Array.from(this.moreItem.querySelectorAll('[data-more-entry]'));
      items.forEach((item) => (item.hidden = false));
      entries.forEach((entry) => (entry.hidden = true));
      this.moreItem.hidden = true;

      if (!this.railFits()) {
        this.moreItem.hidden = false;
        const isExternal = (item) => item.classList.contains('header__rail-item--ext');
        const order = [
          ...items.filter((item) => !isExternal(item)).reverse(),
          ...items.filter(isExternal).reverse(),
        ];
        for (const item of order) {
          if (this.railFits()) break;
          item.hidden = true;
          const entry = entries.find((candidate) => candidate.dataset.moreEntry === item.dataset.railItem);
          if (entry) entry.hidden = false;
        }
      }

      if (this.openItem && this.openItem.hidden) this.close();
    }
  }

  if (!customElements.get('header-mega')) customElements.define('header-mega', HeaderMega);

  // HeaderDrawer is a global class from global.js (classic scripts share top-level classes).
  if (typeof HeaderDrawer === 'function' && !customElements.get('header-drawer-v2')) {
    class HeaderDrawerV2 extends HeaderDrawer {
      // Only the burger summary drives the drawer; group summaries toggle natively.
      bindEvents() {
        this.mainSummary = this.mainDetailsToggle.querySelector(':scope > summary');
        this.mainSummary.addEventListener('click', this.onSummaryClick.bind(this));

        this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
        this.desktopQuery.addEventListener('change', (event) => {
          if (event.matches && this.mainDetailsToggle.hasAttribute('open')) {
            this.closeMenuDrawer(event, this.mainSummary);
          }
        });
      }

      // Escape closes the whole drawer, wherever focus is inside it.
      onKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE' || !this.mainDetailsToggle.hasAttribute('open')) return;
        this.closeMenuDrawer(event, this.mainSummary);
      }

      openMenuDrawer(summaryElement) {
        super.openMenuDrawer(summaryElement);
        this.mainSummary.setAttribute('aria-expanded', 'true');
      }

      // Ride collapses every <details> on close; the groups stay expanded here.
      closeMenuDrawer(event, elementToFocus) {
        super.closeMenuDrawer(event, elementToFocus);
        if (!elementToFocus) return;
        this.mainSummary.setAttribute('aria-expanded', 'false');
        this.querySelectorAll('details.header-drawer__group').forEach((group) => (group.open = true));
      }
    }

    customElements.define('header-drawer-v2', HeaderDrawerV2);
  }
})();
